import React, { ReactNode } from 'react';

import { rem, Flex, Header, useColorModeValue } from '@vimeo/bokeh';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { BokehMenu, BokehMenuItem } from '@vimeo/live-core/components/bokeh';
import {
  EPollState,
  IPoll,
  IPollItemMenuActionItem,
  IPollItemActions,
} from '@vimeo/live-core/types/interaction_tools';

import { createInteractionDomName } from '../../../../libs/shared/utils/dom/identifying';
import { useSourceHoverControls } from '../../../broadcaster/tools/shared/useSourceHoverControls';
import { PollItemActions } from './PollItemActions';
import { PollOptionVotesBar } from './PollOptionVotesBar';

interface IPollItemProps extends IBaseDomElementProps {
  poll: IPoll;
  isPollShownInActiveScene?: boolean;
  isAnyPollPublished?: boolean;
  canOpenPoll: boolean;
  menuAttach: string;
  itemActions: IPollItemActions;
  menuActions: Array<IPollItemMenuActionItem>;
  childrenControls?: ReactNode;
}

export function PollItem({
  className = createInteractionDomName('poll-item'),
  isPollShownInActiveScene,
  isAnyPollPublished,
  poll,
  poll: { state, question, options },
  canOpenPoll,
  menuAttach,
  itemActions,
  menuActions,
  childrenControls,
}: IPollItemProps) {
  const borderColor: string = useColorModeValue('stroke', 'transparent');
  const { onMouseEnter, onMouseLeave, isMenuOpen, isItemHovered, onToggleMenu, onCloseMenu } =
    useSourceHoverControls();

  const totalVotesCount: number = options.reduce((acc, option) => acc + option.votesCount, 0);
  const isPollCompleted: boolean = state === EPollState.CLOSED || state === EPollState.ARCHIVED;
  const isPollArchived: boolean = state === EPollState.ARCHIVED;

  return (
    <Flex
      className={className}
      position={'relative'}
      padding={rem(16)}
      rowGap={rem(16)}
      direction={'column'}
      background={'fill-component'}
      _hover={{
        background: 'fill-component-hover',
      }}
      border={'1px solid'}
      borderRadius={rem(8)}
      borderColor={borderColor}
      cursor={isPollCompleted ? 'default' : 'pointer'}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Flex
        className={createInteractionDomName(className, 'header')}
        justifyContent={'space-between'}
        alignItems={'flex-start'}
      >
        <Header
          className={createInteractionDomName(className, 'question')}
          size={'xs'}
          minHeight={rem(24)}
          margin={0}
          maxWidth={isPollArchived ? '100%' : '83%'}
        >
          {question}
        </Header>

        <Flex
          className={createInteractionDomName(className, 'controls')}
          visibility={isItemHovered ? 'visible' : 'hidden'}
        >
          {isPollArchived ? null : (
            <>
              {childrenControls}

              {!menuActions.length ? null : (
                <BokehMenu
                  onClose={onCloseMenu}
                  placement={menuAttach}
                  className={createInteractionDomName(className, 'menu', 'button')}
                  isOpen={isMenuOpen}
                  onClick={onToggleMenu}
                  menuList={
                    <>
                      {menuActions.map(({ label, key, onClick, icon }) => (
                        <BokehMenuItem
                          key={key}
                          className={createInteractionDomName(className, 'menu', 'item')}
                          onClick={() => {
                            if (onClick) {
                              onClick();
                            }

                            if (!['removeFromScene', 'addToScene'].includes(key)) {
                              onCloseMenu();
                            }
                          }}
                          icon={icon as React.ReactElement}
                        >
                          {label}
                        </BokehMenuItem>
                      ))}
                    </>
                  }
                />
              )}
            </>
          )}
        </Flex>
      </Flex>

      {options.map((opt) => (
        <PollOptionVotesBar
          className={createInteractionDomName(className, 'votes')}
          key={opt.id}
          option={opt}
          state={state}
          totalCount={totalVotesCount}
        />
      ))}

      <PollItemActions
        isPollShownInActiveScene={isPollShownInActiveScene}
        poll={poll}
        canOpenPoll={canOpenPoll}
        itemActions={itemActions}
        isAnyPollPublished={isAnyPollPublished}
      />
    </Flex>
  );
}
